import { EditOutlined } from '@ant-design/icons';
import { Button, Col, Modal, Row, Spin, Tabs } from 'antd';
import { employeeApi } from 'Api/employee';
import { salaryApi } from 'Api/salaryDetails';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastText } from 'utils/utils';
import Documents from 'components/AddEmployee/Documents';
import SalaryDetails from 'components/AddEmployee/SalaryDetails';
import { PermissionContext } from 'components/Global/AuthLayout';
import dayjs from 'dayjs';
import { CloseSvg, DeductionsIconSvg, EarningsIconSvg } from 'utils/svgs';
import DeductionTab from './DeductionTab';
import EarningsTab from './EarningsTab';
import EmployeeDetails from './EmployeeDetails';
import FamilyHistory from './FamilyHistory';
import styles from './index.module.scss';
import './index.scss';
import PaymentDetails from './PaymentInfo';
import TaxationDetails from './TaxationDetaills';
import OpeningBalance from 'components/AddEmployee/OpeningBalance';

type Props = {
	onCancel: () => void;
	isViewOnlyEmployeeDetails: boolean;
	isEditEmployeeFromView: () => void;
};

const ViewEmployee = (props: Props) => {
	const navigate = useNavigate();
	const { onCancel, isViewOnlyEmployeeDetails, isEditEmployeeFromView } =
		props;

	const [searchParams] = useSearchParams();
	const [employeeDetails, setEmployeeDetails] = useState<any>();
	const [activeKey, setActiveKey] = useState('employmentDetails');
	const [employeeId, setEmployeeId] = useState<string>('');
	const [isEmployeeSalaryExists, setIsEmployeeSalaryExists] =
		useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [earningsYear, setEarningsYear] = useState<any>(dayjs());
	const [earningsMonth, setEarningsMonth] = useState(
		new Date().getMonth() + 1
	);
	const [deductionsYear, setDeductionsYear] = useState<any>(dayjs());
	const [deductionsMonth, setDeductionsMonth] = useState(
		new Date().getMonth() + 1
	);
	const [isUSD, setIsUSD] = useState<boolean>(false);
	const permissions = useContext(PermissionContext).allowedPermissions;

	const changeTab = (key: any) => {
		setActiveKey(key);
	};

	const onEarningsYearChange = (selectedYear: any) => {
		setEarningsYear(selectedYear);
	};

	const onEarningsMonthChange = (month: number) => {
		setEarningsMonth(month);
	};

	const onDeductionsYearChange = (selectedYear: any) => {
		setDeductionsYear(selectedYear);
	};

	const onDeductionsMonthChange = (month: number) => {
		setDeductionsMonth(month);
	};

	useEffect(() => {
		const employeeIdFromSearchParams = searchParams.get('employeeId');
		if (employeeIdFromSearchParams) {
			getEmployeeDetail(employeeIdFromSearchParams);
			getSalaryDetails(employeeIdFromSearchParams);
		}
	}, []);

	const getEmployeeDetail = async (employeeId: string) => {
		try {
			setIsLoading(true);
			const employeeData = await employeeApi.getEmployeeDetailsById(
				employeeId
			);
			setEmployeeDetails(employeeData?.data?.data);
			setEmployeeId(employeeData?.data?.data?.employeeId);
			setIsUSD(employeeData?.data?.data?.isUSD);
		} catch (error: any) {
			let message = 'Something went wrong in fetching employee details';
			if (error.response.data.message) {
				message = error.response.data.message;
			}
			navigate('/');
			toastText(message, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const getSalaryDetails = async (employeeId: string) => {
		try {
			const salaryData = await salaryApi.getEmployeeSalary({
				employeeId,
			});
			if (salaryData?.data?.data?.length > 0) {
				setIsEmployeeSalaryExists(true);
			}
		} catch (error: any) {
			let message =
				'Something went wrong in fetching employee salary details';
			toastText(message, 'error');
		}
	};

	let items = [
		{
			key: 'employmentDetails',
			label: 'Employment',
			children: <EmployeeDetails employeeData={employeeDetails} />,
		},
		{
			key: 'taxationDetails',
			label: 'Taxation',
			children: <TaxationDetails employeeData={employeeDetails} />,
		},
		{
			key: 'salaryDetails',
			label: 'Salary',
			children: (
				<SalaryDetails
					employeeId={employeeId}
					setIsEmployeeSalaryExists={setIsEmployeeSalaryExists}
					isViewMode={true}
					isUsd={isUSD}
				/>
			),
		},
		{
			key: 'paymentInfo',
			label: 'Payment Info',
			children: <PaymentDetails />,
		},
		{
			key: 'documents',
			label: 'Documents',
			children: <Documents isViewMode={true} />,
		},
		{
			key: 'familyHistory',
			label: 'Family History',
			children: <FamilyHistory />,
		},
		{
			key: 'earnings',
			label: (
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						verticalAlign: 'middle',
					}}
				>
					<EarningsIconSvg /> <span>Earnings</span>
				</span>
			),
			children: (
				<EarningsTab
					onYearChange={onEarningsYearChange}
					onMonthChange={onEarningsMonthChange}
					year={earningsYear}
					activeTab={activeKey}
					month={earningsMonth}
					isUsd={isUSD}
					employeeId={employeeId}
					isViewMode={true}
				/>
			),
		},
		{
			key: 'deductions',
			label: (
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						verticalAlign: 'middle',
					}}
				>
					<DeductionsIconSvg /> <span>Deductions</span>
				</span>
			),
			children: (
				<DeductionTab
					onYearChange={onDeductionsYearChange}
					onMonthChange={onDeductionsMonthChange}
					year={deductionsYear}
					month={deductionsMonth}
					isUsd={isUSD}
					employeeId={employeeId}
					isViewMode={true}
					activeTab={activeKey}
				/>
			),
		},
		...(searchParams.get('employeeId') && employeeDetails?.OpeningBalance
			? [
					{
						key: 'openingBalance',
						label: <span>Opening Balance</span>,
						destroyInactiveTabPane: true,
						children: <OpeningBalance />,
					},
			  ]
			: []),
	].filter(Boolean);

	if (!permissions.includes('View_Employment')) {
		items = items.filter(
			(item) =>
				item.key !== 'employmentDetails' &&
				item.key !== 'taxationDetails'
		);
	}

	if (!permissions.includes('View_Salary')) {
		items = items.filter((item) => item.key !== 'salaryDetails');
	}

	if (!permissions.includes('View_Payment_Info')) {
		items = items.filter((item) => item.key !== 'paymentInfo');
	}
	if (!permissions.includes('View_Documents')) {
		items = items.filter((item) => item.key !== 'documents');
	}
	if (!permissions.includes('View_Earnings')) {
		items = items.filter((item) => item.key !== 'earnings');
	}
	if (!permissions.includes('View_Deductions')) {
		items = items.filter((item) => item.key !== 'deductions');
	}
	if (!permissions.includes('View_Opening_Balance')) {
		items = items.filter((item) => item.key !== 'openingBalance');
	}

	const handleEditEmployee = () => {
		const employeeIdFromSearchParams = searchParams.get('employeeId');
		onCancel();
		navigate(`/employees?employeeId=${employeeIdFromSearchParams}`);
		isEditEmployeeFromView();
	};

	return (
		<Modal
			open={isViewOnlyEmployeeDetails}
			width={'100%'}
			onCancel={onCancel}
			className="preview-container"
			closable={false}
			style={{ borderRadius: 0 }}
			footer={[]}
		>
			{isLoading ? (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '90vh',
					}}
				>
					<Spin
						size="large"
						style={{
							fontSize: '100px',
							color: '#584495',
						}}
					/>
				</div>
			) : (
				<div>
					<Row className={styles['preview-container__top']}>
						<div className={styles['preview-container__top-title']}>
							<b>Employee Profile</b>
							<span className={styles['divider']}></span>

							<span
								className={
									styles[
										'preview-container__content--new-employee-header-first'
									]
								}
							>
								 {employeeId}
								{(employeeDetails?.firstName ||
									employeeDetails?.midName ||
									employeeDetails?.lastName) && (
									<>
										{' '}
										<span className={styles['divider']}></span>{' '}
										{[
											employeeDetails?.firstName,
											employeeDetails?.midName,
											employeeDetails?.lastName,
										]
											.filter(Boolean)
											.join(' ')}
									</>
								)}
							</span>
						</div>
						<Col
							span={2}
							className={
								styles[
									'preview-container__content--new-employee-header-second'
								]
							}
						>
							<div style={{ textAlign: 'right' }}>
								<Button
									type="default"
									className={styles['edit-button']}
									onClick={handleEditEmployee}
								>
									<EditOutlined />
									Edit
								</Button>
							</div>
							<div
								className={
									styles['preview-container__top-close']
								}
								onClick={() => {
									setEmployeeId('');
									onCancel();
								}}
							>
								<CloseSvg />
							</div>
						</Col>
					</Row>
					<div className={styles['preview-container__content']}>
						<div
							className={
								styles[
									'preview-container__content--new-employee-header-tabs'
								]
							}
						>
							<Tabs
								defaultActiveKey={activeKey}
								activeKey={activeKey}
								destroyInactiveTabPane={true}
								items={items}
								onChange={(key) => changeTab(key)}
								style={{
									fontSize: '18px',
									marginBottom: '40px',
								}}
							/>
						</div>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default ViewEmployee;
